import axios, { AxiosRequestConfig } from 'axios';
import { TAxiosResponse } from 'types/apis/api';
import { SERVER_DOMAIN } from 'constants/auth';
import { ROUTE_PATH } from 'constants/routePath';
import { postAccessTokenReissue } from 'apis/auth';

type TCreateService = {
  url: string;
  options: AxiosRequestConfig;
};

export type TCustomAxiosError = {
  code: number;
  message: string;
  data: string | number | null;
};

const axiosConfig = {
  baseURL: SERVER_DOMAIN,
  withCredentials: true,
};

export const axiosInstance = axios.create(axiosConfig);

export const createService = async <TData>({
  url,
  options,
}: TCreateService) => {
  const { data } = await axiosInstance<TAxiosResponse<TData>>(url, options);

  if (data.code === 200) {
    return data;
  }

  throw data;
};

export const isTCustomAxiosError = (
  error: unknown
): error is TCustomAxiosError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'data' in error
  );
};

export const reissueInstance = axios.create(axiosConfig);

export const reissueService = async <TData>({
  url,
  options,
}: TCreateService) => {
  const { data } = await reissueInstance<TAxiosResponse<TData>>(url, options);

  if (data.code === 200) {
    const { data: result } = data;

    return result;
  }

  throw data;
};

axiosInstance.interceptors.response.use(async (response) => {
  if (response.data.code === 401) {
    try {
      const data = await postAccessTokenReissue();
      const newHeaders = {
        ...response.config.headers,
        Authorization: `Bearer ${data}`,
      };

      axiosInstance.defaults.headers.Authorization = `Bearer ${data}`;
      sessionStorage.setItem('accessToken', `${data}`);
      return axiosInstance({ ...response.config, headers: newHeaders });
    } catch (error) {
      return Promise.reject(error);
    }
  } else if (response.data.code === 403) {
    window.location.href = ROUTE_PATH.common.FORBIDDEN;
  }
  return response;
});
